import { Component, Input, OnInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import * as moment from "moment";
import { catchError, throwError } from "rxjs";
import {
  CompetencyGroupDTO,
  CompetencyGroupService,
  EducationDTO,
  EmployeeDTO,
  ExternalProjectDTO,
  ProfileCompetencyDTO,
  ProfileDTO,
  ProfileEducationDTO,
  ProfileProjectCompetencyDTO,
  ProfileProjectDTO,
  ProfileService,
  ProjectProfileExportDTO,
} from "src/shared/api/generated";
import { CompetencyPillCommand } from "src/shared/models/competency-pill-command.model";
import { EmployeeCompetencyLevelService } from "src/shared/services/employee-competency-level.service";

@Component({
  selector: "app-modify-profile-modal",
  templateUrl: "./modify-profile-modal.component.html",
  styleUrls: ["./modify-profile-modal.component.scss"],
})
export class ModifyProfileModalComponent implements OnInit {
  @Input() profile: ProfileDTO = {} as ProfileDTO;

  editedExternalProject: ExternalProjectDTO = {} as ExternalProjectDTO;
  editExternalProjectMode: boolean = false;
  editedExternalProjectIndex: number | null = null;
  addExternalProjectMode: boolean = false;

  editedStudy: string = "";
  editedStudyIndex: number | null = null;
  editStudiesMode: boolean = false;
  addStudyMode: boolean = false;

  seniorities = EmployeeDTO.SeniorityEnum;

  currentDate: Date = new Date();

  competencyDisplayModes = Object.values(
    ProfileCompetencyDTO.ShowCompetencyAsEnum
  );
  levelEditedProfileCompetency: { id: number; level: string } | null = null;

  technologicalCompetencyGroup: CompetencyGroupDTO = {} as CompetencyGroupDTO;
  languageCompetencyGroup: CompetencyGroupDTO = {} as CompetencyGroupDTO;

  allProfileCompetencies: ProfileCompetencyDTO[] = [];
  technologicalCompetencies: ProfileCompetencyDTO[] = [];
  languageCompetencies: ProfileCompetencyDTO[] = [];
  minimalShownLevel: string = this.employeeCompetencyLevelService.getDefaultOption();

  changeProjectCompetencyVisibility = (
    competency: ProfileProjectCompetencyDTO
  ) => {
    competency.visible = !competency.visible;
  };
  isProjectCompetencyActive = (competency: ProfileProjectCompetencyDTO) => {
    return !competency.visible;
  };
  commands: CompetencyPillCommand[] = [
    {
      icon: "eye",
      function: this.changeProjectCompetencyVisibility,
      class: ["pill-dnd"],
      alt: {
        isAltActive: this.isProjectCompetencyActive,
        icon: "eye-slash",
        function: this.changeProjectCompetencyVisibility,
        class: ["pill-dnd", "pill-unselected"],
      },
    },
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private competencyGroupService: CompetencyGroupService,
    private profileService: ProfileService,
    public employeeCompetencyLevelService: EmployeeCompetencyLevelService
  ) {}

  ngOnInit() {
    if (this.profile.profileProjectOrder) {
      this.profile.internalProjects!.sort((a, b) => this.sortByProjectOrder(a.id!, b.id!));
    }
    
    this.competencyGroupService.getAllCompetencyGroups().subscribe({
      next: (competencyGroups) => {
        this.technologicalCompetencyGroup = competencyGroups.find(
          (cg) => cg.name === "Technological"
        )!;
        this.languageCompetencyGroup = competencyGroups.find(
          (cg) => cg.name === "Language"
        )!;
        this.technologicalCompetencies = this.getTechnologicalCompetencies();
        this.languageCompetencies = this.getLanguageCompetencies();
      },
    });
    this.currentDate = new Date(moment.now());
  }

  private sortByProjectOrder(a: number, b: number) {
    return this.profile.profileProjectOrder!.indexOf(a) - this.profile.profileProjectOrder!.indexOf(b);
  }

  isLastProject(internalProject: ProfileProjectDTO) {
    return this.profile.internalProjects![this.profile.internalProjects!.length - 1].id === internalProject.id;
  }

  isFirstProject(internalProject: ProfileProjectDTO) {
    return this.profile.internalProjects![0].id === internalProject.id;
  }

  moveProjectUp(internalProject: ProfileProjectDTO) {
    let index = this.profile.internalProjects!.indexOf(internalProject);
    [this.profile.internalProjects![index], this.profile.internalProjects![index - 1]] =
     [this.profile.internalProjects![index - 1], this.profile.internalProjects![index]];
  }

  moveProjectDown(internalProject: ProfileProjectDTO) {
    let index = this.profile.internalProjects!.indexOf(internalProject);
    [this.profile.internalProjects![index], this.profile.internalProjects![index + 1]] =
     [this.profile.internalProjects![index + 1], this.profile.internalProjects![index]];
  }

  changeProjectVisibility(project: ProfileProjectDTO) {
    project.visible = !project.visible;
  }

  changeShowSeniority() {
    this.profile.showSeniority = !this.profile.showSeniority;
  }

  removeExternalProject(project: ExternalProjectDTO) {
    if (this.editExternalProjectMode) {
      return;
    }
    this.profile.externalProjects?.splice(
      this.profile.externalProjects?.indexOf(project),
      1
    );
  }

  startEditingExternalProject(index: number) {
    if (this.editExternalProjectMode) {
      return;
    }
    this.editExternalProjectMode = true;
    this.editedExternalProject = { ...this.profile.externalProjects![index] };
    this.editedExternalProjectIndex = index;
  }

  saveProjectEdit() {
    if (
      this.editedExternalProject.projectName === "" ||
      this.editedExternalProject.year === ""
    ) {
      return;
    }

    if (this.editedExternalProjectIndex != null) {
      this.profile.externalProjects![this.editedExternalProjectIndex] =
        this.editedExternalProject;
    }
    this.editedExternalProject = {} as ExternalProjectDTO;
    this.editExternalProjectMode = false;
    this.editedExternalProjectIndex = null;
    this.addExternalProjectMode = false;
  }

  cancelProjectEdit() {
    if (this.addExternalProjectMode) {
      this.profile.externalProjects?.pop();
    }
    this.editedExternalProject = {} as ExternalProjectDTO;
    this.editExternalProjectMode = false;
    this.editedExternalProjectIndex = null;
    this.addExternalProjectMode = false;
  }

  addExternalProject() {
    if (this.editExternalProjectMode) {
      return;
    }

    this.editedExternalProjectIndex =
      this.profile.externalProjects!.push({
        id: undefined,
        year: "",
        projectName: "",
        task: undefined,
      }) - 1;
    this.editExternalProjectMode = true;
    this.addExternalProjectMode = true;
    this.editedExternalProject = {
      ...this.profile.externalProjects![this.editedExternalProjectIndex],
    };
  }

  getTasksByInternalProject(internalProject: ProfileProjectDTO) {
    return internalProject.project?.tasks?.filter((task) => task !== "");
  }

  getTechnologicalCompetencies(): ProfileCompetencyDTO[] {
    return (
      this.profile.competencies?.filter(
        (c) =>
          c.competency?.competencyGroup.rootGroupId ===
          this.technologicalCompetencyGroup.id
      ) ?? []
    );
  }

  getLanguageCompetencies() {
    return (
      this.profile.competencies?.filter(
        (c) =>
          c.competency?.competencyGroup.rootGroupId ===
          this.languageCompetencyGroup.id
      ) ?? []
    );
  }

  changeCompetencyVisibility(competency: ProfileCompetencyDTO) {
    competency.visible = !competency.visible;
    if (this.levelEditedProfileCompetency?.id === competency.id) {
      this.levelEditedProfileCompetency = null;
    }
  }

  hideCompetency(competency: ProfileCompetencyDTO) {
    competency.visible = false;
  }

  changeTechnologicalCompetencyDisplayMode(event: any) {
    this.getTechnologicalCompetencies().forEach(
      (c) => (c.showCompetencyAs = event.target.value)
    );
  }

  changeLanguageCompetencyDisplayMode(event: any) {
    this.getLanguageCompetencies().forEach(
      (c) => (c.showCompetencyAs = event.target.value)
    );
  }

  changeEducationVisibility(education: ProfileEducationDTO) {
    education.visible = !education.visible;
  }

  saveProfile(): void {
    if (this.editExternalProjectMode) {
      return;
    }
    this.profile.internalProjects?.forEach((project) => {
      project.profileProjectCompetencyOrder = project?.project
        ?.profileProjectCompetencies
        ? project.project.profileProjectCompetencies.map(
            (competency) => competency.id!
          )
        : [];
    });
    let projectOrder = this.profile.internalProjects?.map((project) => project.id!) || [];
    this.profile.profileProjectOrder = projectOrder;
    this.profileService
      .updateProfile(this.profile)
      .pipe(catchError((error) => throwError(() => error.error)))
      .subscribe((updatedProfile) => this.activeModal.close(updatedProfile));
  }

  isEducationExpired(educationDTO: EducationDTO) {
    if (!educationDTO.validUntil) {
      return false;
    }
    return new Date(educationDTO.validUntil) < this.currentDate;
  }

  onMinimalShownLevelSave() {
    this.allProfileCompetencies = this.getTechnologicalCompetencies().concat(
      this.getLanguageCompetencies()
    );
    this.allProfileCompetencies.forEach((c) => {
      if (c.level && this.employeeCompetencyLevelService.getCompetencyLevel(c.level) < this.employeeCompetencyLevelService.getCompetencyLevel(this.minimalShownLevel)) this.hideCompetency(c);
    });
  }

  onStartEditingLevel(competency: ProfileCompetencyDTO): void {
    if (!competency.visible) return;

    this.levelEditedProfileCompetency = {
      id: competency.id!,
      level: competency.level!,
    };
  }

  onSaveLevelEditing(competency: ProfileCompetencyDTO): void {  

    competency.level = this.levelEditedProfileCompetency!.level;
    this.levelEditedProfileCompetency = null;
  }

  onCancelLevelEditing(): void {
    this.levelEditedProfileCompetency = null;
  }
}
